import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import PublicationListsBrowse from "./components/PublicationListsBrowse";
import SetListDomains from "./components/SetListDomains";
import api from "../../../api";
import { PublicationListSet } from "../../../types/campaignTypes";
import { countActiveDomains, resolveSetLists } from "./publicationListSetUtils";
import { errorToaster, successToaster } from "../../../helpers/toasters";
import { usePublicationLists } from "./usePublicationLists";

type ModalState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; set: PublicationListSet }
  | { mode: "delete"; set: PublicationListSet };

type MenuAnchor = { el: HTMLElement; set: PublicationListSet } | null;

// ─── Styled components ───────────────────────────────────────────────────────

const TabRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const Header = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
});

const PrimaryButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.coralHover, transform: "translateY(-1px)", boxShadow: "none" },
  "&.Mui-disabled": { background: theme.palette.brand.coral, color: theme.palette.brand.white, opacity: 0.6 },
}));

const DangerButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.errorRed,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#DC2626", boxShadow: "none" },
  "&.Mui-disabled": { background: theme.palette.brand.errorRed, color: theme.palette.brand.white, opacity: 0.6 },
}));

const SecondaryButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px solid ${theme.palette.brand.gray200}`,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.gray50, boxShadow: "none" },
}));

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const CountCell = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

const MoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  color: theme.palette.brand.gray500,
  padding: 4,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    background: theme.palette.brand.gray100,
    color: theme.palette.brand.gray900,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  minWidth: 180,
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const DangerMenuItem = styled(StyledMenuItem)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.errorRed,
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "48px 24px",
  textAlign: "center" as const,
  color: theme.palette.brand.gray500,
  fontSize: 14,
}));

const LoadingState = styled("div")({
  padding: "48px 24px",
  display: "flex",
  justifyContent: "center",
});

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "32px 0",
}));

const ModalBody = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  paddingTop: 8,
});

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────

const PublicationListSetsTab: FC = () => {
  const { selectedAdvertiserId: brandgenAdvertiserId, allLists } = usePublicationLists();

  const [sets, setSets] = useState<PublicationListSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState<ModalState>({ mode: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [formName, setFormName] = useState("");
  const [formListIds, setFormListIds] = useState<string[]>([]);

  const fetchSets = useCallback(async () => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    try {
      const { payload } = await api.getPublicationListSets(brandgenAdvertiserId);
      setSets(Array.isArray(payload) ? payload : []);
    } catch {
      setSets([]);
    } finally {
      setIsLoading(false);
    }
  }, [brandgenAdvertiserId]);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const openCreate = () => {
    setFormName("");
    setFormListIds([]);
    setModal({ mode: "create" });
  };

  const openEdit = (set: PublicationListSet) => {
    setFormName(set.name);
    // Drop IDs that no longer resolve so saving cleans up dangling references.
    setFormListIds(resolveSetLists(allLists, set.publicationListIds).map((list) => list.id));
    setModal({ mode: "edit", set });
  };

  const closeModal = () => setModal({ mode: "closed" });

  const toggleFormList = (listId: string) => {
    setFormListIds((ids) => (ids.includes(listId) ? ids.filter((id) => id !== listId) : [...ids, listId]));
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      errorToaster("Please enter a name for this set.", 4000);
      return;
    }
    if (formListIds.length === 0) {
      errorToaster("Please select at least one publication list.", 4000);
      return;
    }
    if (!brandgenAdvertiserId) return;
    setIsSaving(true);
    try {
      if (modal.mode === "create") {
        await api.createPublicationListSet({
          name: formName.trim(),
          brandgenAdvertiserId,
          publicationListIds: formListIds,
        });
        successToaster("Publication list set created.");
      } else if (modal.mode === "edit") {
        await api.updatePublicationListSet(modal.set.id, {
          name: formName.trim(),
          publicationListIds: formListIds,
        });
        successToaster("Publication list set updated.");
      }
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to save. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (modal.mode !== "delete") return;
    setIsSaving(true);
    try {
      await api.deletePublicationListSet(modal.set.id);
      successToaster("Publication list set deleted.");
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to delete. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    toggleExpanded(id);
  };

  const isFormOpen = modal.mode === "create" || modal.mode === "edit";

  return (
    <TabRoot>
      <Header>{brandgenAdvertiserId && <PrimaryButton onClick={openCreate}>Create New Set</PrimaryButton>}</Header>

      {!brandgenAdvertiserId ? (
        <AdvertiserPrompt>Please select an advertiser to manage publication list sets.</AdvertiserPrompt>
      ) : isLoading ? (
        <LoadingState>
          <CircularProgress size={32} />
        </LoadingState>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>No publication list sets yet. Create one to get started.</EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Lists</Th>
                <Th>Domains</Th>
                <Th>Last Modified</Th>
                <Th style={{ width: 120 }} />
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const memberLists = resolveSetLists(allLists, set.publicationListIds);
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow onClick={() => toggleExpanded(set.id)}>
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {memberLists.length.toLocaleString()}
                        </CountCell>
                      </Td>
                      <Td>{countActiveDomains(memberLists).toLocaleString()}</Td>
                      <Td>{set.updatedAt ? new Date(set.updatedAt).toLocaleDateString() : ""}</Td>
                      <Td>
                        <MoreButton
                          type="button"
                          aria-label={`Actions for ${set.name}`}
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            setMenuAnchor({ el: e.currentTarget, set });
                          }}
                        >
                          <MoreIcon />
                        </MoreButton>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={5}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <SetListDomains lists={memberLists} />
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StyledMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) openEdit(menuAnchor.set);
            setMenuAnchor(null);
          }}
        >
          Edit
        </StyledMenuItem>
        <DangerMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) setModal({ mode: "delete", set: menuAnchor.set });
            setMenuAnchor(null);
          }}
        >
          Delete
        </DangerMenuItem>
      </Menu>

      <Dialog open={isFormOpen} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {modal.mode === "create" ? "Create Publication List Set" : "Edit Publication List Set"}
        </DialogTitle>
        <DialogContent>
          <ModalBody>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Set name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <PublicationListsBrowse
              selectionMode
              bodyMaxHeight={360}
              selectedListIds={formListIds}
              onListSelect={toggleFormList}
            />
          </ModalBody>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <SecondaryButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={modal.mode === "delete"} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Publication List Set</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14 }}>
            Are you sure you want to delete <strong>{modal.mode === "delete" ? modal.set.name : ""}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <SecondaryButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <DangerButton onClick={handleDelete} disabled={isSaving}>
            {isSaving ? "Deleting..." : "Delete"}
          </DangerButton>
        </DialogActions>
      </Dialog>
    </TabRoot>
  );
};

export default PublicationListSetsTab;
